import { useCallback, useState, type ReactNode } from 'react'
import { Sheet } from './Sheet.tsx'
import { Button, Paragraph } from './ui.tsx'

/** Potvrzeni panelem appky misto window.confirm, ktere v PWA vypada jako chyba prohlizece. */
export function ConfirmSheet({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Zpět',
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  children?: ReactNode
  confirmLabel: string
  cancelLabel?: string | undefined
  danger?: boolean | undefined
  busy?: boolean | undefined
  onConfirm(): void
  onClose(): void
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <div className="button-row">
          <Button onClick={onClose}>{cancelLabel}</Button>
          <Button
            kind={danger ? undefined : 'primary'}
            className={danger ? 'grow btn-notfound' : 'grow'}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {typeof children === 'string' ? <Paragraph tone="fg">{children}</Paragraph> : children}
    </Sheet>
  )
}

export interface ConfirmOptions {
  title: string
  text: string
  confirmLabel: string
  danger?: boolean | undefined
}

/**
 * `await confirm({…})` misto window.confirm — panel appky, vysledek jako slib.
 * Volajici vykresli `confirmSheet`.
 */
export function useConfirm() {
  const [state, setState] = useState<(ConfirmOptions & { resolve(ok: boolean): void }) | null>(null)
  const confirm = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ ...options, resolve })),
    [],
  )
  const finish = (ok: boolean) => {
    state?.resolve(ok)
    setState(null)
  }
  const confirmSheet = (
    <ConfirmSheet
      open={state !== null}
      title={state?.title ?? ''}
      confirmLabel={state?.confirmLabel ?? ''}
      danger={state?.danger}
      onConfirm={() => finish(true)}
      onClose={() => finish(false)}
    >
      {state?.text}
    </ConfirmSheet>
  )
  return { confirm, confirmSheet }
}
